import { useEffect, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  FUTURE_SELF_HORIZON_HINTS,
  FUTURE_SELF_HORIZON_LABELS,
  FUTURE_SELF_HORIZONS,
  FUTURE_SELF_PROMPTS,
  futureSelfProgress,
  type FutureSelfHorizon,
  type FutureSelfPromptKey,
} from '@mlm/shared/coaching';
import {
  Block,
  Chip,
  CoachButton,
  ErrorNote,
  Eyebrow,
  inputClass,
  Loading,
  Meter,
  Screen,
} from '../components/primitives';
import { coaching } from '../lib/api';
import { qk, useFutureSelf } from '../lib/queries';

/**
 * SC-18: the version of themselves the member is choosing to grow toward.
 *
 * Nothing on this screen is generated. The whole point is that they wrote it,
 * and a model filling in what their five-year life looks like would be writing
 * the one thing the app has no business writing.
 */
export function FutureSelfPage() {
  const qc = useQueryClient();
  const saved = useFutureSelf();
  const [horizon, setHorizon] = useState<FutureSelfHorizon>('one_year');
  const [drafts, setDrafts] = useState<Partial<Record<FutureSelfPromptKey, string>>>({});

  const stored = saved.data?.horizons.find((h) => h.horizon === horizon);

  // Switching horizon loads that document; it never carries a draft across.
  useEffect(() => {
    setDrafts(stored?.answers ?? {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [horizon, saved.data]);

  const save = useMutation({
    mutationFn: () =>
      coaching.saveFutureSelf({ horizon, answers: drafts as Record<string, string> }),
    meta: { successMessage: 'Saved.' },
    onSuccess: () => void qc.invalidateQueries({ queryKey: qk.futureSelf }),
  });

  if (saved.isPending) return <Loading />;
  if (saved.isError) return <ErrorNote error={saved.error} retry={() => void saved.refetch()} />;

  const progress = futureSelfProgress(drafts);

  return (
    <Screen
      back="/manifest"
      eyebrow="Future self"
      title="Meet the person you are becoming"
      subtitle="Write it in the present tense, as though it were already ordinary. You can come back and change any of it."
    >
      <section className="border-b border-border px-5 py-6">
        <Eyebrow className="mb-3">When are you imagining this?</Eyebrow>
        <div className="flex flex-wrap gap-2">
          {FUTURE_SELF_HORIZONS.map((h) => (
            <Chip key={h} selected={horizon === h} onClick={() => setHorizon(h)}>
              {FUTURE_SELF_HORIZON_LABELS[h]}
            </Chip>
          ))}
        </div>
        <p className="mt-4 text-sm leading-relaxed text-ink/70">
          {FUTURE_SELF_HORIZON_HINTS[horizon]}
        </p>
        <div className="mt-6">
          <Meter label="Written" value={progress} />
        </div>
      </section>

      <section className="space-y-7 border-b border-border px-5 py-6">
        {FUTURE_SELF_PROMPTS.map((p) => (
          <label key={p.key} className="block">
            <span className="eyebrow">{p.label}</span>
            <span className="mt-1.5 block text-sm leading-relaxed text-ink/70">{p.prompt}</span>
            <textarea
              value={drafts[p.key] ?? ''}
              onChange={(e) => setDrafts((d) => ({ ...d, [p.key]: e.target.value }))}
              rows={p.long ? 7 : 3}
              maxLength={2000}
              className={`${inputClass} mt-3 resize-none`}
            />
          </label>
        ))}
      </section>

      <section className="border-b border-border px-5 py-6">
        <CoachButton kind="gold" onClick={() => save.mutate()} disabled={save.isPending}>
          {save.isPending ? 'Saving…' : 'Save'}
        </CoachButton>
        {stored && (
          <p className="eyebrow mt-4">
            Last saved {new Date(stored.updatedAt).toLocaleDateString()}
          </p>
        )}
      </section>

      <Block eyebrow="Using it">
        This is the version of you that today's choices are for. Master Kala can see it, so you can
        ask what a decision looks like from where they are standing.
      </Block>
    </Screen>
  );
}
